import {
    cents,
    defineFilters,
    enumOne,
    enumOrAny,
    multiEnum,
    text,
    triState,
    type ApiValue,
    type FilterField,
    type FilterOption
} from '@/components/kit/filters'
import type { ICard } from '@/types/card'

import {
    CARD_SORT_OPTIONS,
    CARD_TYPE_OPTIONS,
    CONDITION_OPTIONS,
    GAME_OPTIONS,
    LANGUAGE_OPTIONS,
    PRINTING_OPTIONS,
    SINGLE_CONDITIONS
} from './card-constants'

const ORDER_OPTIONS: FilterOption<'asc' | 'desc'>[] = [
    { value: 'desc', label: 'Descending' },
    { value: 'asc', label: 'Ascending' }
]

/**
 * Merged-away cards, filtered in the browser rather than on the wire.
 *
 * `/cards/search` hardcodes `includeMerged: true` and accepts no parameter for it
 * (`searchCardsSchema` in `routes/admin/cards.ts`), so there is nothing to send. The
 * field lives in URL state like any other filter and `toApi` returns `undefined`, so
 * it can never reach the endpoint as an ignored parameter; `applyMergedFilter` does
 * the work over the page that came back. The chip says so, because a filter that only
 * covers the current page next to a server-side total would otherwise read as broken.
 */
function mergedField(): FilterField<'' | 'true' | 'false'> {
    const base = triState({ label: 'Merged' })
    return {
        ...base,
        toApi: () => undefined,
        describe: value => (value === 'true' ? 'Only merged (this page)' : 'Hidden from this page')
    }
}

/**
 * `condition` is deliberately absent from the spec's API projection: what goes on the
 * wire depends on the Type filter as much as on the multi-select, so it is derived in
 * `cardSearchParams` rather than declared per field.
 */
export const cardFilters = defineFilters(
    {
        search: text({ label: 'Search' }),
        type: enumOne(CARD_TYPE_OPTIONS, 'all', { label: 'Type' }),
        game: enumOrAny(GAME_OPTIONS, { label: 'Game' }),
        language: enumOrAny(LANGUAGE_OPTIONS, { label: 'Language' }),
        conditions: multiEnum(CONDITION_OPTIONS, { label: 'Condition', apiKey: 'condition', encode: 'repeat' }),
        printings: multiEnum(PRINTING_OPTIONS, { label: 'Printing', apiKey: 'printing', encode: 'repeat' }),
        priceMin: cents({ label: 'Min price', apiKey: 'minPrice' }),
        priceMax: cents({ label: 'Max price', apiKey: 'maxPrice' }),
        merged: mergedField(),
        sortBy: enumOne(CARD_SORT_OPTIONS, '', { label: 'Sort' }),
        sortOrder: enumOne(ORDER_OPTIONS, 'desc', { label: 'Order' })
    },
    { defaultLimit: 24, maxLimit: 100 }
)

export type CardSearch = ReturnType<typeof cardFilters.parse>

/** Repeated query keys, with a lone value duplicated. See `MultiEncoding`. */
function repeat(values: readonly string[]): string[] {
    return values.length === 1 ? [values[0]!, values[0]!] : [...values]
}

/**
 * The merged filter, applied to the rows the endpoint returned. Client-side because
 * the endpoint has no equivalent parameter; see `mergedField`.
 */
export function applyMergedFilter(cards: ICard[], value: CardSearch['merged']): ICard[] {
    if (value === '') return cards
    return cards.filter(card => Boolean(card.mergedInto) === (value === 'true'))
}

/**
 * The wire params for `/cards/search`.
 *
 * Two things the generic engine cannot express. First, Type is not a filter the API
 * has -- it is a shorthand for a condition set, and it only reaches the wire that
 * way. Second, `sortBy: ''` means relevance, which the endpoint spells by omitting
 * the parameter rather than by sending an empty string.
 *
 * Note what is *not* sent: `includeHidden`. The handler hardcodes it to `true` and
 * ignores the parameter, so the legacy Singles-only view promised to exclude hidden
 * cards and never did. Rather than reproduce a control that does nothing, hidden
 * cards are always in the results and always badged. `includeMerged` is the same:
 * hardcoded to `true`, no parameter, so the Merged filter runs client-side.
 */
export function cardSearchParams(search: CardSearch): Record<string, ApiValue> {
    const params = cardFilters.toApiParams(search)
    delete params.sortBy
    if (search.sortBy !== '') params.sortBy = search.sortBy

    if (search.type === 'sealed') params.condition = repeat(['Sealed'])
    else if (search.conditions.length > 0) params.condition = repeat(search.conditions)
    else if (search.type === 'singles') params.condition = repeat(SINGLE_CONDITIONS)
    else delete params.condition

    return params
}
